import { forwardRef, useState, useEffect } from "react";
import ModelIndex, { MessagesTypeProps, ModelIndexHandle } from "@components/layout/ModelIndex";
import styles from "./style.module.scss";
import moment from "moment";
import { formatMoneyValue } from "@utils/Currency";
import { CreditnoteApi, CreditnoteDataType } from "@/api/CreditnoteApi";
import { OpenSendAtvDocumentPubSub, } from "@utils/pubsubs/PubSub";
import { useAppSelector } from "@/store/hooks";
import { sendEDebitnoteData } from "@utils/EDocument";
import { cancelAcceptedNoteWithCounter } from "@utils/noteCancellation";
import { DebitnoteApi, DebitnoteDataType } from "@/api/DebitnoteApi";
import { showAlert } from "@utils/Alert";
import { faEnvelope, faEye, faPaperPlane, faPrint, faXmark } from "@fortawesome/free-solid-svg-icons";
import LoadingScreen from "@components/content/LoadingScreen";
import { renderEDocumentStatusOffCanvasTrigger } from "@utils/ModelIndexUtils";
import { EasyposEInvoice } from "@/atv/submitInvoice";
import { EDOCUMENT_SUBMITION_STATUS_CODE, EDOCUMENT_SUBMITION_STATUS_SENT } from "@/atv/eInvoiceApi";
import SendAtvDocumentToEmailOffCanvas from "@components/layout/Offcanvas/SendAtvDocumentToEmailOffCanvas";
import { DebitnoteFormDataKeys } from "@/generated/DebitnoteFormDataKeys";
import { DebitnoteRelatedAttributes } from "@/formTypes/DebitnoteDataType";
import { generateAndUploadPdf } from "@utils/htmlExport";
import { documentPdfS3Key, documentPrintPath } from "@utils/documentPdf";

type DebitnoteModelIndexProps = {
  messages?: MessagesTypeProps;
  mandatoryFilters?: Array<Record<string, unknown>>;
  fetchData?: (...args: Parameters<typeof CreditnoteApi.fetchAll>) => ReturnType<typeof CreditnoteApi.fetchAll>;
};

const DebitnoteModelIndex = forwardRef<ModelIndexHandle, DebitnoteModelIndexProps>(
  ({ messages, mandatoryFilters, fetchData }, ref) => {

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const suscriptionData = useAppSelector(state => state.suscription.suscription)
    const resolvedMessages = {
      noResults: "No hay notas de Debito creadas aún",
      ...messages,
    };

    const resolvedMandatoryFilters = mandatoryFilters ?? [];
    const resolvedFetch = fetchData ?? ((...args) => DebitnoteApi.fetchAll(...args));
    const [easyposEInvoice, setEasyposEInvoice] = useState<EasyposEInvoice>()

    useEffect(() => {
      if (suscriptionData) {
        setEasyposEInvoice(new EasyposEInvoice(suscriptionData))
      }
    }, [suscriptionData]);

    const triggerEmailSend = (item: DebitnoteDataType) => {
      OpenSendAtvDocumentPubSub.publish(
        {
          documentId: item.id,
          openOffCanvas: true,
          documentKind: "Debitnote",
          suscription_numero: Number(suscriptionData?.numero || 0),
          documentkey: `${item.document_key}`,
          clientEmail: `${item.email || item.invoice?.email}`,
          emailBody: `${suscriptionData?.invoice_email}`,
          emailSubject: `${suscriptionData?.nombre} -- Nota de Debito #${item.numero}`,
          Suscription: {
            email: `${suscriptionData?.email}`
          }
        })
    }

    const cancelDebitnote = async (item: DebitnoteDataType) => {
      const debitnote = await DebitnoteApi.get(`${item.id}`, {
        selectionSet: [
          ...DebitnoteFormDataKeys.filter(k => !DebitnoteRelatedAttributes.includes(k)),
          "currency.*",
          "client.*",
          "debitnoteProducts.*",
          "debitnoteProducts.product.*",
        ]
      })
      try {
        setIsLoading(true)
        const result = await cancelAcceptedNoteWithCounter({
          note: debitnote,
          kind: "Debitnote",
          reason: `Anuluar Nota de Debito ${item.document_key}`,
          touchStock: true,
          easyposEInvoice,
          einvoiceActive: Boolean(suscriptionData?.einvoice_status),
        })
        showAlert("success", `Se ha creado correctamente la nota de Credito #${result.counterNumero} para la cancelacion de la nota de debito #${item.numero} `);
      } catch (err) {
        showAlert("danger", (err as Error).message);
      } finally {
        setIsLoading(false);
      }
    }

    const resendENote = async (noteId: string, item: DebitnoteDataType) => {
      setIsLoading(true);
      try {
        if (easyposEInvoice) {
          // Regenerar antes de reenviar: si el PDF no se genero al emitir, este es el
          // unico camino que puede dejarlo en S3 antes de que salga el correo.
          await generateAndUploadPdf(
            documentPrintPath("Debitnote", noteId),
            documentPdfS3Key("Debitnote", suscriptionData?.numero, `${item.document_key}`),
          )
          const response = await sendEDebitnoteData(easyposEInvoice, noteId)
          if (response.status == EDOCUMENT_SUBMITION_STATUS_CODE[EDOCUMENT_SUBMITION_STATUS_SENT]) {
            showAlert("success", "La Nota de Debito ha sido reenviada correctamente, el estado de la nota de debito se actualizara una vez Tributacion de una respuesta sobre el documento")
          } else {
            showAlert("danger", "La Nota de Debito no ha sido reenviada correctamente")
          }
        } else {
          showAlert("danger", "No fue possible enviar el document a hacienda ")
        }
      } finally {
        setIsLoading(false)
      }
    }

    return (
      <>
        <ModelIndex<CreditnoteDataType>
          ref={ref}
          meiliConfig={{
            index: "debitnotes",
            sort: ["created:desc"]
          }}
          messages={resolvedMessages}
          mandatoryFilters={resolvedMandatoryFilters}
          fetchData={resolvedFetch}
          filterProps={[
            {
              key: "sequence",
              filterType: "contains"
            },
            {
              key: "document_key",
              filterType: "contains"
            },
            {
              key: "numero",
              filterType: "eq",
              type: "number"
            }
          ]}
          primaryProperties={[
            {
              key: "sequence",
              highlighted: true,
              className: styles.sequence
            },
            {
              key: (d) => {
                if ("invoiceNombreFactura" in d) {
                  return (d as any).invoiceNombreFactura
                }
                return d.client?.nombre || ""
              },
              alignment: "Start"
            },
          ]}
          primaryPropertiesSizing={
            [
              { sm: "4", xs: "12" },
              { sm: "8", xs: "12" }
            ]
          }
          secondaryProperties={[
            {
              key: (data) => {
                const date = moment(data.created, "YYYY-MM-DD")
                return date.format("DD-MM-YYYY")
              },
              title: "Fecha",
            },
            {
              key: (data) => renderEDocumentStatusOffCanvasTrigger(data, { model: "Debitnote", id: `${data.id}` }),
              title: "Estado ATV"
            },
            {
              key: (data) => {
                if ("currency_id" in data) {
                  return formatMoneyValue(data.total, (data as any).currency_id)
                }
                return formatMoneyValue(data.total, data.currency.currency_id)
              },
              title: "Total",
              highlighted: true
            }
          ]}
          secondaryPropertiesSizing={
            [
              { sm: "4", md: "2" },
              { sm: "4", md: "3" },
              { sm: "4", md: "2" }
            ]
          }
          listActions={[
            {
              isVisible: (data) => !data.edocument_status_payload,
              onClick: (id, item) => resendENote(id, item),
              actionTitle: "Reenviar Nota de Debito al ATV",
              iconProps: { icon: faPaperPlane }
            },
            {
              onClick: (id, item) => cancelDebitnote(item),
              actionTitle: "Anular Nota de Debito",
              iconProps: { icon: faXmark, className: styles.cancelBtn },
              needConfirm: true,
              confirmProps: {
                confirmMessage: "Esta seguro que desea anular esta nota de Debito? Esta accion creara una nota de Credito",
              }
            },
            {
              onClick: (id, item) => triggerEmailSend(item),
              actionTitle: "Enviar Por Correo ",
              iconProps: { icon: faEnvelope }
            },
            {
              href: "/user/debitnotes/view/{:id}",
              actionTitle: "Ver Nota de Debito",
              target: "_blank",
              iconProps: { icon: faEye }
            },
            {
              href: "/user/debitnotes/print/{:id}/0/1",
              actionTitle: "Imprimir Nota de Debito",
              target: "_blank",
              iconProps: { icon: faPrint }
            }
          ]}
        >
        </ModelIndex>

        <SendAtvDocumentToEmailOffCanvas
          title="Envio de Nota de Debito a Email" />
        <LoadingScreen isLoading={isLoading} />
      </>
    );
  }
);

DebitnoteModelIndex.displayName = "DebitnoteModelIndex";
export default DebitnoteModelIndex;